import { ReactNode, useMemo, useState } from 'react';
import {
  ArrowUpIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  ChevronDownIcon,
  CubeIcon,
  DocumentTextIcon,
  EllipsisVerticalIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { DraggableDetailPanel } from '../../components/ui/DraggableDetailPanel';
import {
  PortalDeliveriesData,
  PortalDeliveryItem,
  PortalDeliveryStageTone,
  PortalDeliveryStatus,
} from './portalDeliveriesMock';

type StatusFilter = 'all' | PortalDeliveryStatus;
type DetailTab = 'overview' | 'files' | 'comments' | 'history';

const statusFilters: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'Todas' },
  { id: 'development', label: 'Em desenvolvimento' },
  { id: 'validation', label: 'Em validação' },
  { id: 'approved', label: 'Aprovadas' },
  { id: 'planned', label: 'Previstas' },
];

const statusLabel: Record<PortalDeliveryStatus, string> = {
  development: 'Em desenvolvimento',
  validation: 'Em validação',
  approved: 'Aprovada',
  planned: 'Prevista',
};

const statusClass: Record<PortalDeliveryStatus, string> = {
  development: 'bg-primary-50 text-primary-700',
  validation: 'bg-orange-50 text-orange-700',
  approved: 'bg-emerald-50 text-emerald-700',
  planned: 'bg-secondary-100 text-secondary-600',
};

const stageToneClass: Record<PortalDeliveryStageTone, string> = {
  blue: 'bg-sky-50 text-sky-800',
  purple: 'bg-violet-50 text-violet-800',
  red: 'bg-red-50 text-red-700',
  amber: 'bg-amber-50 text-amber-800',
  green: 'bg-emerald-50 text-emerald-800',
  sky: 'bg-cyan-50 text-cyan-800',
  orange: 'bg-orange-50 text-orange-800',
  slate: 'bg-secondary-100 text-secondary-700',
};

const stageDotClass: Record<PortalDeliveryStageTone, string> = {
  blue: 'bg-sky-500',
  purple: 'bg-violet-500',
  red: 'bg-red-500',
  amber: 'bg-amber-500',
  green: 'bg-emerald-500',
  sky: 'bg-cyan-500',
  orange: 'bg-orange-500',
  slate: 'bg-secondary-500',
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

type PortalProjectDeliveriesProps = {
  deliveries: PortalDeliveriesData;
};

export function PortalProjectDeliveries({ deliveries }: PortalProjectDeliveriesProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>('del-test-env');
  const [detailTab, setDetailTab] = useState<DetailTab>('overview');
  const [approvedIds, setApprovedIds] = useState<string[]>([]);
  const [adjustmentRequestedIds, setAdjustmentRequestedIds] = useState<string[]>([]);

  const counts = useMemo(() => {
    const base = {
      all: deliveries.items.length,
      development: 0,
      validation: 0,
      approved: 0,
      planned: 0,
    };
    for (const item of deliveries.items) {
      const effective = approvedIds.includes(item.id) ? 'approved' : item.status;
      base[effective] += 1;
    }
    return base;
  }, [approvedIds, deliveries.items]);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('pt-BR');
    return deliveries.items.filter((item) => {
      const effectiveStatus = approvedIds.includes(item.id) ? 'approved' : item.status;
      if (statusFilter !== 'all' && effectiveStatus !== statusFilter) return false;
      if (!normalized) return true;
      const haystack = `${item.title} ${item.description} ${item.version} ${item.stageLabel}`.toLocaleLowerCase(
        'pt-BR',
      );
      return haystack.includes(normalized);
    });
  }, [approvedIds, deliveries.items, query, statusFilter]);

  const selected =
    filtered.find((item) => item.id === selectedId) ??
    deliveries.items.find((item) => item.id === selectedId) ??
    null;

  function effectiveStatus(item: PortalDeliveryItem): PortalDeliveryStatus {
    return approvedIds.includes(item.id) ? 'approved' : item.status;
  }

  function handleApprove(itemId: string) {
    setApprovedIds((current) => (current.includes(itemId) ? current : [...current, itemId]));
    setAdjustmentRequestedIds((current) => current.filter((id) => id !== itemId));
  }

  function handleRequestAdjustments(itemId: string) {
    setAdjustmentRequestedIds((current) => (current.includes(itemId) ? current : [...current, itemId]));
  }

  return (
    <div className={`relative space-y-5 ${selected ? 'xl:pr-[24rem]' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-secondary-900">Entregas do projeto</h2>
          <p className="mt-1 max-w-2xl text-sm text-secondary-600">
            Acompanhe entregáveis, homologação e aceite das versões disponibilizadas.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-[12rem] rounded-xl border border-secondary-200 bg-white px-4 py-3">
            <div className="mb-1 flex items-center justify-between text-xs text-secondary-500">
              <span>Progresso das entregas</span>
              <span className="font-semibold text-secondary-800">{deliveries.progressPercent}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-secondary-100">
              <div
                className="h-full rounded-full bg-primary-500"
                style={{ width: `${deliveries.progressPercent}%` }}
              />
            </div>
          </div>
          <button type="button" className="erp-btn-primary inline-flex items-center gap-1.5">
            <PlusIcon className="h-4 w-4" aria-hidden />
            Registrar entrega
            <ChevronDownIcon className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-2">
          {statusFilters.map((filter) => {
            const active = statusFilter === filter.id;
            const count = counts[filter.id];
            return (
              <button
                key={filter.id}
                type="button"
                onClick={() => setStatusFilter(filter.id)}
                className={`rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${
                  active
                    ? 'bg-primary-500 text-white'
                    : 'border border-secondary-200 bg-white text-secondary-700 hover:bg-secondary-50'
                }`}
              >
                {filter.label} ({count})
              </button>
            );
          })}
        </div>
        <div className="relative ml-auto min-w-[14rem] flex-1 sm:flex-none sm:w-64">
          <MagnifyingGlassIcon
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400"
            aria-hidden
          />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar entregas..."
            className="w-full rounded-lg border border-secondary-200 bg-white py-2 pl-9 pr-3 text-sm text-secondary-800 placeholder:text-secondary-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm font-semibold text-secondary-700"
        >
          <FunnelIcon className="h-4 w-4" aria-hidden />
          Filtros
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-secondary-200 bg-secondary-50 text-xs font-medium text-secondary-500">
              <tr>
                <th className="px-4 py-3 font-medium">Entrega</th>
                <th className="px-3 py-3 font-medium">Descrição</th>
                <th className="px-3 py-3 font-medium">Etapa</th>
                <th className="px-3 py-3 font-medium">Responsável</th>
                <th className="px-3 py-3 font-medium">Data prevista</th>
                <th className="px-3 py-3 font-medium">Status</th>
                <th className="px-3 py-3 font-medium">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => {
                const status = effectiveStatus(item);
                const selectedRow = selectedId === item.id;
                return (
                  <tr
                    key={item.id}
                    onClick={() => {
                      setSelectedId(item.id);
                      setDetailTab('overview');
                    }}
                    className={`cursor-pointer border-t border-secondary-100 ${
                      selectedRow
                        ? 'border-l-4 border-l-primary-500 bg-primary-50/70'
                        : 'hover:bg-secondary-50'
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
                          <CubeIcon className="h-5 w-5" aria-hidden />
                        </span>
                        <div>
                          <p className="font-semibold text-secondary-900">{item.title}</p>
                          <p className="text-xs text-secondary-500">{item.version}</p>
                        </div>
                      </div>
                    </td>
                    <td className="max-w-xs px-3 py-3 text-secondary-600">
                      <p className="line-clamp-2">{item.description}</p>
                    </td>
                    <td className="px-3 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${stageToneClass[item.stageTone]}`}
                      >
                        {item.stageLabel}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="inline-flex items-center gap-2">
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-50 text-[10px] font-semibold text-primary-700">
                          {initials(item.ownerName)}
                        </span>
                        <span className="whitespace-nowrap text-secondary-700">{item.ownerName}</span>
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-secondary-600">{item.dueLabel}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusClass[status]}`}>
                        {statusLabel[status]}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        className="rounded-lg p-1 text-secondary-400 hover:bg-secondary-100 hover:text-secondary-700"
                        aria-label="Mais opções"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <EllipsisVerticalIcon className="h-5 w-5" aria-hidden />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-secondary-500">Nenhuma entrega com esse filtro.</p>
          ) : null}
        </div>
      </div>

      {selected ? (
        <DeliveryDetailDrawer
          item={{
            ...selected,
            status: effectiveStatus(selected),
            waitingClient:
              effectiveStatus(selected) === 'validation' && !adjustmentRequestedIds.includes(selected.id),
          }}
          activeTab={detailTab}
          onTabChange={setDetailTab}
          onClose={() => setSelectedId(null)}
          adjustmentRequested={adjustmentRequestedIds.includes(selected.id)}
          onApprove={() => handleApprove(selected.id)}
          onRequestAdjustments={() => handleRequestAdjustments(selected.id)}
        />
      ) : null}
    </div>
  );
}

function DeliveryDetailDrawer({
  item,
  activeTab,
  onTabChange,
  onClose,
  adjustmentRequested,
  onApprove,
  onRequestAdjustments,
}: {
  item: PortalDeliveryItem;
  activeTab: DetailTab;
  onTabChange: (tab: DetailTab) => void;
  onClose: () => void;
  adjustmentRequested: boolean;
  onApprove: () => void;
  onRequestAdjustments: () => void;
}) {
  const checklistDone = item.checklist.filter((entry) => entry.done).length;
  const canAct = item.status === 'validation';

  return (
    <DraggableDetailPanel
      resetKey={item.id}
      header={
        <div className="flex items-start justify-between gap-3 border-b border-secondary-100 px-4 py-4">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
              <CubeIcon className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-secondary-900">
                {item.title} {item.version}
              </h3>
              <p className="mt-0.5 text-xs text-secondary-500">{item.stageLabel}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-secondary-400 hover:bg-secondary-50 hover:text-secondary-700"
            aria-label="Fechar detalhes"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden />
          </button>
        </div>
      }
      footer={
        canAct && activeTab === 'overview' ? (
          <div className="space-y-2 p-4">
            <button
              type="button"
              onClick={onApprove}
              disabled={item.status === 'approved'}
              className="erp-btn-primary flex w-full items-center justify-center gap-2"
            >
              <CheckIcon className="h-4 w-4" aria-hidden />
              Aprovar entrega
            </button>
            <button
              type="button"
              onClick={onRequestAdjustments}
              className="erp-btn-secondary flex w-full items-center justify-center gap-2"
            >
              <ChatBubbleLeftRightIcon className="h-4 w-4" aria-hidden />
              Solicitar ajustes
            </button>
          </div>
        ) : undefined
      }
    >
      <div className="border-b border-secondary-200 px-2">
        <nav className="-mb-px flex gap-1 overflow-x-auto" aria-label="Detalhe da entrega">
          {(
            [
              ['overview', 'Visão geral'],
              ['files', `Arquivos (${item.files.length})`],
              ['comments', `Comentários (${item.comments.length})`],
              ['history', 'Histórico'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => onTabChange(id)}
              className={`shrink-0 rounded-none border-b-2 px-3 py-2.5 text-sm font-medium ${
                activeTab === id
                  ? 'border-primary-500 text-primary-700'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {activeTab === 'overview' ? (
          <div className="space-y-4">
            <section className="rounded-xl border border-secondary-200 bg-secondary-50 px-3 py-3">
              <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusClass[item.status]}`}>
                {statusLabel[item.status]}
              </span>
              <p className="mt-2 text-sm text-secondary-700">
                {item.status === 'approved'
                  ? 'Entrega aprovada e registrada no histórico do projeto.'
                  : adjustmentRequested
                    ? 'Ajustes solicitados. A equipe Baruk foi notificada.'
                    : item.waitingClient
                      ? 'Aguardando a aprovação do cliente.'
                      : item.status === 'development'
                        ? 'Entrega em desenvolvimento pela equipe Baruk.'
                        : 'Entrega prevista no cronograma do projeto.'}
              </p>
            </section>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-700">
                  {initials(item.ownerName)}
                </span>
                <div>
                  <p className="text-xs font-medium text-secondary-500">Responsável</p>
                  <p className="text-sm font-semibold text-secondary-900">{item.ownerName}</p>
                  <p className="text-xs text-secondary-500">{item.ownerRole}</p>
                </div>
              </div>
              <DetailField label="Entrega prevista" value={item.dueLongLabel} />
              <DetailField
                label="Etapa"
                value={
                  <span className="inline-flex items-center gap-1.5">
                    <span className={`h-2 w-2 rounded-full ${stageDotClass[item.stageTone]}`} />
                    {item.stageLabel}
                  </span>
                }
              />
              <DetailField
                label="Prioridade"
                value={
                  <span className="inline-flex items-center gap-1 text-red-700">
                    <ArrowUpIcon className="h-3.5 w-3.5" aria-hidden />
                    {item.priorityLabel}
                  </span>
                }
              />
            </div>

            <section>
              <div className="mb-1 flex items-center justify-between text-xs text-secondary-500">
                <span>Progresso</span>
                <span className="font-semibold text-secondary-800">{item.progressPercent}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-secondary-100">
                <div className="h-full rounded-full bg-primary-500" style={{ width: `${item.progressPercent}%` }} />
              </div>
            </section>

            <section>
              <p className="text-xs font-medium text-secondary-500">Descrição</p>
              <p className="mt-1 text-sm leading-relaxed text-secondary-700">{item.detailDescription}</p>
            </section>

            {item.checklist.length > 0 ? (
              <section>
                <p className="text-sm font-semibold text-secondary-900">
                  Checklist de validação ({checklistDone} de {item.checklist.length} concluídos)
                </p>
                <ul className="mt-2 space-y-2">
                  {item.checklist.map((entry) => (
                    <li key={entry.id} className="flex items-start justify-between gap-3 text-sm text-secondary-700">
                      <span className="inline-flex items-start gap-2">
                        <span
                          className={`mt-0.5 flex h-4 w-4 items-center justify-center rounded border ${
                            entry.done
                              ? 'border-primary-500 bg-primary-500 text-white'
                              : 'border-secondary-300 bg-white'
                          }`}
                        >
                          {entry.done ? <CheckIcon className="h-3 w-3" aria-hidden /> : null}
                        </span>
                        <span className={entry.done ? 'text-secondary-500 line-through' : ''}>{entry.label}</span>
                      </span>
                      {entry.dueLabel ? (
                        <span className="shrink-0 text-xs text-secondary-400">{entry.dueLabel}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </div>
        ) : null}

        {activeTab === 'files' ? (
          <ul className="space-y-2">
            {item.files.length === 0 ? (
              <li className="text-sm text-secondary-500">Nenhum arquivo nesta entrega.</li>
            ) : (
              item.files.map((file) => (
                <li
                  key={file.id}
                  className="flex items-center gap-3 rounded-xl border border-secondary-200 bg-secondary-50 px-3 py-2.5"
                >
                  <DocumentTextIcon className="h-5 w-5 text-secondary-500" aria-hidden />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-secondary-900">{file.name}</p>
                    <p className="text-xs text-secondary-500">{file.sizeLabel}</p>
                  </div>
                </li>
              ))
            )}
          </ul>
        ) : null}

        {activeTab === 'comments' ? (
          <ul className="space-y-4">
            {item.comments.length === 0 ? (
              <li className="text-sm text-secondary-500">Nenhum comentário nesta entrega.</li>
            ) : (
              item.comments.map((comment) => (
                <li key={comment.id} className="flex gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-[10px] font-semibold text-primary-700">
                    {initials(comment.authorName)}
                  </span>
                  <div>
                    <p className="text-sm text-secondary-800">
                      <span className="font-semibold">{comment.authorName}</span>
                      <span className="text-secondary-400"> · {comment.whenLabel}</span>
                    </p>
                    <p className="mt-1 text-sm text-secondary-700">{comment.body}</p>
                  </div>
                </li>
              ))
            )}
          </ul>
        ) : null}

        {activeTab === 'history' ? (
          <ul className="space-y-3">
            {item.history.length === 0 ? (
              <li className="text-sm text-secondary-500">Nenhum histórico registrado.</li>
            ) : (
              item.history.map((entry) => (
                <li key={entry.id} className="rounded-xl border border-secondary-200 px-3 py-2.5">
                  <p className="text-sm font-semibold text-secondary-900">{entry.title}</p>
                  <p className="mt-0.5 text-sm text-secondary-600">{entry.detail}</p>
                  <p className="mt-1 text-xs text-secondary-400">{entry.whenLabel}</p>
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>
    </DraggableDetailPanel>
  );
}

function DetailField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium text-secondary-500">{label}</p>
      <div className="mt-0.5 text-sm font-semibold text-secondary-900">{value}</div>
    </div>
  );
}
